import { lazy, Suspense, useState, useEffect } from 'react'
import { RouterProvider } from 'react-router-dom'
import { router } from './routes'
import {
  SplashScreen,
  ErrorBoundary,
  StreamParticleCanvas,
} from '@/shared/components'
import { TooltipProvider } from '@/shared/components/ui/tooltip'
import { Toaster } from '@/shared/components/ui/toaster'
import { useAuthStore } from '@/features/auth'
import { OperatorSelector, SignInGate, useGoogleAuthStore } from '@/features/auth'
import { getEnvironmentConfigs } from '@/lib/tauri'
import { browserEnvironmentName } from '@/features/auth/lib/staff-session'
import { refreshCredential } from '@/features/auth/lib/token-refresh'
import { api } from '@/lib/api'
import {
  useEnvironmentStore,
  useOperatorStore,
  useTransitionStore,
} from '@/shared/stores'
import { SettingsModal } from '@/shared/components/SettingsModal'
import { EnvironmentModal } from '@/shared/components/EnvironmentModal'
import { DebugBridge } from '@/shared/debug'
import { useQueueStore, OPERATOR_TAGS, type OperatorTag } from '@/features/queue'
import { IS_TAURI } from '@/lib/platform'

/**
 * Log-feature modal — desktop only (Task 4093).
 *
 * `/log-feature` is in DESKTOP_ONLY_PREFIXES and its route is filtered out of
 * the browser router, but this modal is mounted unconditionally at the App root
 * rather than reached through a route. A static import therefore shipped the
 * whole feature (11 of 14 source files) to the browser bundle anyway — the same
 * trap as the four eager route imports, found in review of this task.
 *
 * The modal self-gates on `useLogFeatureStore.isOpen` and nothing in the browser
 * build can open it, so lazily loading it costs nothing and it never mounts.
 */
const LogFeatureModal = IS_TAURI
  ? lazy(() =>
      import('@/features/log-feature').then((m) => ({
        default: m.LogFeatureModal,
      })),
    )
  : null

/**
 * Bootstrap sequence:
 *
 *   1. Show the brand splash for the initial reveal (desktop only; the
 *      browser portal skips it and lands on the sign-in gate or the app).
 *   2. Load the operator list from the Rust layer (`list_operators`). This
 *      also reconciles any persisted operator selection — if a previously
 *      selected consumer_id no longer exists, the splash will surface again.
 *   3. If an operator is selected, fetch its environment configs, hand them
 *      to the environment store, and render the main app. Otherwise render
 *      the OperatorSelector.
 *
 * Once an operator has been picked, the selection is persisted to
 * localStorage (`stream-operator`) and stays locked across app launches.
 * The only way back to the OperatorSelector is the Sidebar footer's
 * "Switch operator" affordance, which calls `useOperatorStore.clearOperator()`.
 *
 * The existing `switchGeneration` machinery is preserved end-to-end. When
 * the operator changes, the environment store reconfigures the API client
 * and bumps the generation, forcing a clean RouterProvider remount.
 */
/**
 * Placeholder while a boot step resolves. Desktop keeps its branded splash; the
 * browser portal shows nothing (just the particle background) — every step it
 * waits on there is a single network call, and a splash only delayed the
 * operator.
 */
const bootPlaceholder = IS_TAURI ? <SplashScreen onComplete={() => {}} /> : null

function App() {
  // Browser: no click-to-continue splash after sign-in — go straight to the app.
  const [showSplash, setShowSplash] = useState(IS_TAURI)
  const [apiReady, setApiReady] = useState(false)
  const [initError, setInitError] = useState<string | null>(null)

  const checkAuth = useAuthStore((s) => s.checkAuth)
  const switchGeneration = useEnvironmentStore((s) => s.switchGeneration)
  const transitionPhase = useTransitionStore((s) => s.phase)

  // Task 3127: Google OAuth access gate. Runs before any other boot step —
  // if the operator does not have a valid `@kodeon.ai` refresh token in the
  // Keychain, render the SignInGate and short-circuit the rest of the boot.
  const googleAuthStatus = useGoogleAuthStore((s) => s.status)
  const checkGoogleAuth = useGoogleAuthStore((s) => s.checkAuth)
  // Browser only: the ID token doubles as the API credential (Task 4092).
  const googleIdToken = useGoogleAuthStore((s) => s.idToken)

  // Step 0: silently check the Google access gate. Runs once on mount and is
  // a pre-requisite for everything else: until the operator is authenticated
  // we do not even fetch the operator list.
  useEffect(() => {
    void checkGoogleAuth()
  }, [checkGoogleAuth])

  const isOperatorLoaded = useOperatorStore((s) => s.isLoaded)
  const operatorLoadError = useOperatorStore((s) => s.loadError)
  const selectedOperator = useOperatorStore((s) => s.selected)
  const loadStaffProfile = useOperatorStore((s) => s.loadStaffProfile)

  // Step 1: resolve the signed-in Google identity to a staff profile (idempotent).
  // Gated on the Google access check passing — no reason to call the backend
  // before we know the user is a valid @kodeon.ai account.
  //
  // Also re-runs when the profile is cleared (`clearOperator()` resets
  // isLoaded) while still signed in. Desktop then shows the OperatorSelector
  // after the reload; without this the browser sat on an empty placeholder
  // with nothing to resolve it.
  useEffect(() => {
    if (googleAuthStatus !== 'authenticated' || isOperatorLoaded) return
    void loadStaffProfile()
  }, [loadStaffProfile, googleAuthStatus, isOperatorLoaded])

  // Watch for unmorph completion: when phase transitions back to 'river',
  // show the splash screen again. This only happens after the user clicks
  // the back button from the OperatorSelector.
  useEffect(() => {
    if (IS_TAURI && transitionPhase === 'river' && !showSplash) {
      // Only show splash if we were previously not showing it (i.e., we're
      // returning from the operator selector, not on initial mount)
      const wasOnOperatorSelector = !selectedOperator
      if (wasOnOperatorSelector) {
        setShowSplash(true)
      }
    }
  }, [transitionPhase, showSplash, selectedOperator])

  // Step 2: once an operator is selected, fetch environment configs and
  // configure the API client. This effect re-runs if the selected operator
  // changes (e.g. user picked a different operator on the splash).
  useEffect(() => {
    // The operator picker is a desktop concept: it selects which provisioned
    // consumer's API key to use. In the browser the signed-in Google identity
    // IS the operator, so gate on the token instead.
    const browserReady = !IS_TAURI && googleIdToken !== null
    if (!browserReady && (!selectedOperator || !selectedOperator.consumer_id)) {
      // No operator yet — clear apiReady so we don't render stale routes.
      setApiReady(false)
      return
    }

    let cancelled = false
    const initializeApi = async () => {
      try {
        if (!IS_TAURI) {
          // Browser portal (Task 4092): there is no Rust layer to fetch
          // per-operator API keys from, and no dev/prod switching — the bundle
          // targets one gateway, fixed at build time. The Google ID token from
          // sign-in IS the credential; the gateway verifies it and swaps it for
          // an internal JWT before proxying downstream.
          const idToken = googleIdToken
          if (!idToken) {
            throw new Error('Signed in but no ID token available')
          }
          // Report the environment honestly. The store defaults to 'prod' and
          // is normally corrected by initialize(), which the browser never
          // calls — leaving the header badge reading PROD while every request
          // goes to whatever VITE_GATEWAY_URL points at. An operator acting on
          // dev data under a PROD badge (or the reverse) is a real hazard, so
          // derive it from the URL actually in use.
          useEnvironmentStore.setState({ active: browserEnvironmentName() })
          api.configure(idToken)
          // Recover from an expired token without signing the operator out.
          // Desktop does not install this: the Rust layer mints a fresh token
          // per call, so a 401 there is genuine.
          api.setCredentialRefresher(refreshCredential)
          setApiReady(true)
          setInitError(null)
          return
        }

        // Desktop only. Unreachable without an operator: the browser branch
        // above returns, and the effect's guard requires one otherwise.
        if (!selectedOperator?.consumer_id) return

        const configs = await getEnvironmentConfigs(
          selectedOperator.consumer_id,
        )
        if (cancelled) return
        useEnvironmentStore.getState().initialize(configs)
        setApiReady(true)
        setInitError(null)
      } catch (err) {
        if (cancelled) return
        const message = err instanceof Error ? err.message : String(err)
        console.error('[App] Failed to initialize API client:', message)
        setInitError(message)
        setApiReady(false)
      }
    }

    void initializeApi()
    return () => {
      cancelled = true
    }
  }, [selectedOperator, googleIdToken])

  const loadScopes = useOperatorStore((s) => s.loadScopes)

  // Step 3: re-check auth identity once the API is ready and on each
  // environment / operator switch. Also bootstrap the operator's scope set
  // so ScopeGate and the sidebar can render immediately. (Task 3552c)
  useEffect(() => {
    if (apiReady) {
      checkAuth()
      void loadScopes()
    }
  // checkAuth and loadScopes are stable Zustand store references.
  // switchGeneration is intentionally excluded: the [selectedOperator] effect
  // already resets apiReady on operator change, so apiReady false→true is
  // sufficient to re-trigger this effect. Including switchGeneration caused
  // React error #185 (infinite render loop) on launch.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiReady])

  // Sync the queue feature's `operatorTag` (used as `assigned_by` on every
  // attention-queue assignment) from the splash-screen-selected operator.
  // The four operators hard-defined in src-tauri (auth.rs::OPERATORS) carry
  // initials that exactly match the backend's allow-list (MJT/BH/JW/TF), so
  // we can use them directly as the OperatorTag with no manual picker.
  //
  // We validate against OPERATOR_TAGS so a future operator added in Rust
  // without a matching backend tag (e.g. a fifth person) silently falls back
  // to `null` here, which the assign call coerces to "" — keeping assignment
  // working even before the backend allow-list is widened.
  const setQueueOperatorTag = useQueueStore((s) => s.setOperatorTag)
  useEffect(() => {
    const initials = selectedOperator?.initials
    if (!initials) {
      setQueueOperatorTag(null)
      return
    }
    if ((OPERATOR_TAGS as readonly string[]).includes(initials)) {
      setQueueOperatorTag(initials as OperatorTag)
    } else {
      setQueueOperatorTag(null)
    }
  }, [selectedOperator, setQueueOperatorTag])

  // ── Render gating ───────────────────────────────────────────────────────
  let content: React.ReactNode

  if (
    googleAuthStatus === 'unauthenticated' ||
    googleAuthStatus === 'signing_in'
  ) {
    // Task 3127: Google access gate. Render this *before* the splash so a
    // signed-out user lands here directly instead of seeing the splash and
    // then a flash of the operator selector.
    content = <SignInGate />
  } else if (
    googleAuthStatus === 'unknown' ||
    googleAuthStatus === 'checking'
  ) {
    // Silent token refresh in flight. Keep the placeholder up — with a valid
    // Keychain (desktop) or stored (browser) token this resolves sub-second
    // and we never flash any sign-in chrome.
    content = bootPlaceholder
  } else if (showSplash) {
    // Initial branded splash. Stays up while loading operators in parallel,
    // and is dismissed by user interaction (click / key) — see SplashScreen.
    content = <SplashScreen onComplete={() => setShowSplash(false)} />
  } else if (operatorLoadError) {
    // Listing operators failed (Rust command threw). Show retry-able error.
    content = (
      <div style={styles.errorContainer}>
        <div style={styles.errorCard}>
          <h1 style={styles.errorTitle}>Could not load your profile</h1>
          <p style={styles.errorMessage}>{operatorLoadError}</p>
          <p style={styles.errorHint}>
            Check the terminal for more details, then retry.
          </p>
          <button
            type="button"
            onClick={() => void loadStaffProfile({ force: true })}
            className="button-primary"
            style={styles.errorButton}
          >
            Retry
          </button>
        </div>
      </div>
    )
  } else if (!isOperatorLoaded) {
    // Still resolving the operator list from Rust.
    content = bootPlaceholder
  } else if (!selectedOperator || !selectedOperator.consumer_id) {
    // No operator chosen yet: render the splash selector.
    content = <OperatorSelector />
  } else if (initError) {
    // Operator chosen but env configs failed.
    content = (
      <div style={styles.errorContainer}>
        <div style={styles.errorCard}>
          <h1 style={styles.errorTitle}>Could not sign in</h1>
          <p style={styles.errorMessage}>{initError}</p>
          <p style={styles.errorHint}>
            Stream could not retrieve API keys for{' '}
            <strong>{selectedOperator.display_name || selectedOperator.consumer_id}</strong>.
            Run <code>gcloud auth application-default login</code> or pick a
            different operator.
          </p>
          <button
            type="button"
            onClick={() =>
              useOperatorStore.getState().clearOperator()
            }
            className="button-secondary"
            style={styles.errorButton}
          >
            Switch operator
          </button>
        </div>
      </div>
    )
  } else if (!apiReady) {
    // Environment configs are loading. Keep showing the placeholder so the
    // user doesn't see a flash of empty UI.
    content = bootPlaceholder
  } else {
    // Operator selected and signed in — render the main app. The selection
    // is persisted; the Sidebar footer is the only path back to the splash.
    content = (
      <TooltipProvider>
        <RouterProvider router={router} key={switchGeneration} />
        <Toaster />
      </TooltipProvider>
    )
  }

  return (
    <ErrorBoundary>
      <DebugBridge />
      {/*
        Unified river → particles canvas. Mounted once at the App root so it
        survives the SplashScreen → OperatorSelector handoff, and provides the
        animated background for both screens. Phase is driven by
        `useTransitionStore`.
      */}
      <StreamParticleCanvas />
      {content}
      <SettingsModal />
      <EnvironmentModal />
      {LogFeatureModal && (
        <Suspense fallback={null}>
          <LogFeatureModal />
        </Suspense>
      )}
    </ErrorBoundary>
  )
}

const styles: Record<string, React.CSSProperties> = {
  errorContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100vw',
    height: '100vh',
    background: 'var(--bg-primary)',
  },
  errorCard: {
    maxWidth: '500px',
    padding: '32px',
    background: 'var(--bg-secondary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-lg)',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  errorTitle: {
    fontSize: '24px',
    fontWeight: 600,
    color: 'var(--error)',
    margin: 0,
  },
  errorMessage: {
    fontSize: '14px',
    color: 'var(--text-primary)',
    margin: 0,
    fontFamily: 'JetBrains Mono, monospace',
    background: 'var(--bg-tertiary)',
    padding: '12px',
    borderRadius: 'var(--radius-sm)',
    wordBreak: 'break-word',
  },
  errorHint: {
    fontSize: '13px',
    color: 'var(--text-secondary)',
    margin: 0,
    lineHeight: 1.5,
  },
  errorButton: {
    alignSelf: 'flex-start',
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: 500,
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
  },
}

export default App
